/** @format */
import { useRouter } from 'next/router';
import React from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';
import { SortBy } from '@toucan/common/services/search/types';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { SortByOption } from '@toucan/client/features/search/config/types';

import { DesktopSort } from './components/desktop';
import { MobileSort } from './components/mobile';
import { useSortByOption } from './hooks/use-sort-by-option';
import { useSortByOptions } from './hooks/use-sort-by-options';

export function Sort({
    className,
    type,
    sort,
    outOfStock,
    getDefaultSortOption,
    showAll,
}: {
    outOfStock: boolean;
    showAll: boolean;
    className?: string;
    type?: ModelSearchResultsType;
    sort: SortBy;
    getDefaultSortOption?: (
        options: SortByOption[]
    ) => SortByOption | undefined;
}) {
    const router = useRouter();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const sortBy = useSortByOption(type, getDefaultSortOption);
    const { categorySortByOptions, defaultSortByOption } = useSortByOptions(
        outOfStock,
        type
    );

    const defaultSortOption = (getDefaultSortOption
        ? getDefaultSortOption(categorySortByOptions)
        : defaultSortByOption) as unknown as SortByOption;

    function handleSortByChange(sortByOption: SortByOption) {
        const isDefaultOption = sortByOption === defaultSortOption;
        const sort = isDefaultOption
            ? undefined
            : `${sortByOption.facet}:${sortByOption.direction}`;

        const pageReset = showAll ? { page: undefined } : {};

        setSearchParamsForSearchResults(router, {
            sort,
            ...pageReset,
        });
    }

    if (isSmallTablet) {
        return (
            <MobileSort
                data-testid="mobile-sort"
                className={className}
                sortByOptions={categorySortByOptions}
                onSortByChange={handleSortByChange}
                sort={sort}
                defaultSortOption={defaultSortOption}
            />
        );
    }

    return (
        <DesktopSort
            className={className}
            sortByOptions={categorySortByOptions}
            sortBy={sortBy}
            onSortByChange={handleSortByChange}
        />
    );
}
